/**
 * ProductListingPage
 *
 * Unified server component for all Product Listing Pages.
 * Handles data fetching, content rendering, and layout for:
 * - All Watches
 * - Watch Collections
 * - Watch Editorial pages
 * - All Straps
 * Strap Materials
 * - All Eyewear
 *
 * ## Usage
 *
 * ```tsx
 * // In a page.tsx file:
 * import { ProductListingPage } from '@/components/listing/ProductListingPage';
 * import { PLP_CONFIGS } from '@/lib/listingData';
 *
 * export default async function AllWatchesPage({ params, searchParams }) {
 *   const { lang } = await params;
 *   return (
 *     <ProductListingPage
 *       config={PLP_CONFIGS['all-watches']}
 *       locale={lang}
 *       searchParams={await searchParams}
 *     />
 *   );
 * }
 * ```
 */

import { getTranslations, setRequestLocale } from 'next-intl/server';
import { notFound } from 'next/navigation';

import { parseSearchParams, resolveAlgoliaLocaleCode, type FacetProductType } from '@/lib/algolia';
import { localeConfig } from '@/lib/configuration/configuration';
import type { ContentfulFragment } from '@/lib/contentful/types';
import {
  fetchListingPageData,
  filterFacetsByConfig,
  generateBreadcrumbs,
  getDefaultHeroSettings,
  type PLPConfig,
} from '@/lib/listingData';

import { CMSRenderer } from '@/components/cms/CmsRenderer';
import { AlgoliaDebugEcho } from '@/components/debug/AlgoliaDebugEcho';
import { ListingFilters, PLPHero, PLPHeroSimple } from '@/components/listing';
import { PdpBreadcrumbsBar } from '@/components/product/PdpBreadcrumbsBar';
import { Breadcrumbs } from '@/components/ui/Breadcrumbs';
import { BRAND, IS_GALLET_SHAPED } from '@/lib/cms/config';
import type { CMSContentItem } from '@/lib/cms/types';
import { devWarn } from '@/lib/utils/devLog';

import styles from './ProductListingPage.module.scss';

// ============================================
// Types
// ============================================

export interface ProductListingPageProps {
  /** PLP configuration from plpConfig.ts */
  config: PLPConfig;

  /** URL locale (e.g., "us-en") */
  locale: string;

  /** URL search parameters */
  searchParams: Record<string, string | string[] | undefined>;

  /**
   * Dynamic parameter value for collection/material/editorial pages.
   * - For watch collections: collection name (e.g., "Navitimer")
   * - For strap materials: material slug (e.g., "leather")
   * - For editorial pages: page slug
   */
  dynamicValue?: string;

  /** Whether in draft/preview mode (bypasses CMS cache) */
  draftMode?: boolean;
}

// ============================================
// Breadcrumb content-pipeline placement (BEC-24794)
// ============================================

/**
 * A CMS `Breadcrumb` entry authored by a content manager (or already injected
 * synthetically). The dedicated Breadcrumb content type is the authoring
 * surface — content managers add it to the page's content list and position
 * it there, like on other pages.
 */
function isBreadcrumbItem(item: CMSContentItem): boolean {
  return item.__typename === 'Breadcrumb';
}

/** The main-banner `Card` a Gallet-shaped PLP anchors the breadcrumb injection on. */
function isHeroBannerCard(item: CMSContentItem): boolean {
  return item.__typename === 'Card' && (item as { type?: string | null }).type === 'HeroBanner';
}

/**
 * Decide where the breadcrumb renders for Gallet-shaped brands (Gallet + UG):
 * as the CMS-authored `Breadcrumb` entry if a content manager already added
 * one, injected synthetically right after the first HeroBanner card, or —
 * with neither available — the legacy top-of-page block. Breitling always
 * keeps the top-of-page block.
 *
 * `CmsRenderer` derives `cmsIndex`/`isFirstItem` from array position, so
 * inserting after the HeroBanner (rather than at the head) leaves everything
 * up to and including it untouched — its `preload` and, for an `InternalHero`
 * card, the `internal-with-arrow` presentation survive unaffected.
 */
function placeBreadcrumb(
  content: CMSContentItem[],
  isGalletShaped: boolean
): { content: CMSContentItem[]; showBlock: boolean } {
  if (!isGalletShaped) {
    return { content, showBlock: true };
  }
  if (content.some(isBreadcrumbItem)) {
    return { content, showBlock: false };
  }
  const heroBannerIndex = content.findIndex(isHeroBannerCard);
  if (heroBannerIndex === -1) {
    devWarn(
      'placeBreadcrumb: no HeroBanner card in content — falling back to the top-of-page breadcrumb block, which QA rejected for Gallet-shaped PLPs (BEC-24794).'
    );
    return { content, showBlock: true };
  }
  const syntheticBreadcrumb = {
    __typename: 'Breadcrumb',
    sys: { id: 'plp-breadcrumb' },
  } as unknown as CMSContentItem;
  return {
    content: [
      ...content.slice(0, heroBannerIndex + 1),
      syntheticBreadcrumb,
      ...content.slice(heroBannerIndex + 1),
    ],
    showBlock: false,
  };
}

// ============================================
// Component
// ============================================

export async function ProductListingPage({
  config,
  locale,
  searchParams,
  dynamicValue,
  draftMode: isDraftMode = false,
}: Readonly<ProductListingPageProps>) {
  // Validate locale
  if (!localeConfig.isValidLocale(locale)) {
    notFound();
  }

  // Enable static rendering for next-intl
  // This MUST be called before any getTranslations calls
  setRequestLocale(locale);

  // Parse URL filters
  const filters = parseSearchParams(searchParams);

  // Get translations FIRST - before calling cached functions
  const tPlp = await getTranslations('plp');
  const tBreadcrumbs = await getTranslations('breadcrumbs');
  const tBadges = await getTranslations('product.badges');

  // Determine collection/slug for dynamic pages
  let collectionName: string | undefined;
  let slug: string | undefined;

  if (config.isDynamic && dynamicValue) {
    if (config.dynamicParam === 'collection') {
      collectionName = dynamicValue;
    } else if (config.dynamicParam === 'slug' || config.dynamicParam === 'material') {
      slug = dynamicValue;
    }
  }

  // Fetch all listing page data via BFF orchestrator
  const data = await fetchListingPageData({
    type: config.cmsType,
    locale,
    filters,
    collectionName,
    slug,
    draftMode: isDraftMode,
    badgeTranslations: tBadges,
    baseFilters: config.baseFilters,
    productType: config.productType as 'Watch' | 'Strap' | 'Eyewear',
  });

  // Get locale code for facet attribute resolution.
  // Resolve against the index so the configured/index variant mismatch
  // (e.g. config EN_GB vs index EN_US) does not drop localized facets here.
  const localeCode = await resolveAlgoliaLocaleCode(locale, config.productType as FacetProductType);

  // Filter facets to only show those configured for this page type
  // This hides filters that are already hard-coded (e.g., collection on collection pages)
  const filteredFacets = filterFacetsByConfig(data.facets, config, localeCode) ?? {};
  const filteredAllFacets = data.allFacets
    ? (filterFacetsByConfig(data.allFacets, config, localeCode) ?? undefined)
    : undefined;

  // Get hero settings (use defaults if no HeroProductListing in CMS)
  const heroSettings = data.heroSettings || getDefaultHeroSettings();

  // Generate product-type-aware fallback title
  // For dynamic pages (e.g., /straps/leather), use formatted dynamic value
  // Otherwise use product type-specific default
  let fallbackTitle = tPlp('title'); // Default: "All Watches"
  if (config.productType === 'Strap') {
    if (dynamicValue) {
      // Material-specific: "Leather Straps", "Rubber Straps", etc.
      const materialName =
        dynamicValue.charAt(0).toUpperCase() + dynamicValue.slice(1).replace(/-/g, ' ');
      fallbackTitle = `${materialName} Straps`;
    } else {
      fallbackTitle = 'All Straps';
    }
  } else if (config.productType === 'Eyewear') {
    fallbackTitle = 'All Sunglasses';
  }

  // Generate breadcrumbs based on config. On Gallet-shaped collection PLPs,
  // collectionName (already formatted, see the prop doc above) names the
  // actual collection instead of the generic heading/title fallback. Gated to
  // Gallet-shaped brands so Breitling's crumb output stays byte-identical to
  // pre-BEC-24794 `v3-wip` (AC8). BEC-24794.
  const breadcrumbs = generateBreadcrumbs(
    config.cmsType,
    heroSettings.heading || fallbackTitle,
    {
      home: tBreadcrumbs('home'),
      collections: tBreadcrumbs('collections'),
      watches: tBreadcrumbs('watches') || tBreadcrumbs('collections'),
      straps: tBreadcrumbs('straps'),
      all: tBreadcrumbs('all'),
    },
    { collectionName, isGalletShaped: IS_GALLET_SHAPED }
  );

  // Determine what to show based on CMS settings.
  // Gallet PLPs use the CMS HeroBanner as the page header (no simple-text hero).
  // Breadcrumbs follow heroSettings.showBreadcrumbs (defaults to true). BEC-24794.
  const isGallet = BRAND === 'gallet';
  const showBreadcrumbs = Boolean(heroSettings.showBreadcrumbs);
  const showSimpleHero = !isGallet;
  const showFilters = config.showFilters !== false && heroSettings.showFilters;
  const hasHeroMedia = !!heroSettings.media;

  // Render the breadcrumb through the CMS content pipeline for Gallet-shaped
  // brands (see placeBreadcrumb above); Breitling always keeps the top-of-page
  // block. Applies uniformly to the real CMS content or the no-CMS-content
  // fallback below — neither of which ever has a HeroBanner, so injection is a
  // no-op there and the block-1 fallback naturally applies. BEC-24794.
  const baseContent: CMSContentItem[] =
    data.content && data.content.length > 0
      ? data.content
      : [
          {
            __typename: 'Placeholder',
            sys: { id: `${config.productLabel}-product-grid` },
            type: 'product listing',
          } as unknown as CMSContentItem,
        ];
  const { content: contentWithBreadcrumb, showBlock: showBreadcrumbsBlock } = showBreadcrumbs
    ? placeBreadcrumb(baseContent, IS_GALLET_SHAPED)
    : { content: baseContent, showBlock: false };

  // E-Boutique services data (matches ServiceItem interface)
  const eBoutiqueServices = {
    services: [
      {
        sys: { id: 'shipping' },
        icon: 'truck',
        title: 'Free shipping',
        description: 'On all orders',
      },
      {
        sys: { id: 'returns' },
        icon: 'refresh',
        title: 'Free returns',
        description: '30-day guarantee',
      },
      {
        sys: { id: 'gifting' },
        icon: 'gift',
        title: 'Gifting services',
        description: 'Complimentary',
      },
      {
        sys: { id: 'advisor' },
        icon: 'advisor',
        title: 'Personal advisor',
        description: 'Expert guidance',
      },
    ],
  };

  // Extra data for CMSRenderer placeholders
  const extraData = {
    productGrid: {
      products: data.products,
      pagination: data.pagination,
      // Product type info for correct terminology
      productLabel: config.productLabel,
      productType: config.productType,
      // Always render inline filter bar; hide controls when showFilters is false
      ...(config.filterBarPosition === 'inline' && {
        filters: {
          facets: filteredFacets,
          activeFilters: filters,
          totalResults: data.totalHits,
          position: 'inline' as const,
          allFacets: filteredAllFacets,
          currentSort: filters.sortBy || 'relevance',
          productLabel: config.productLabel,
          hideControls: !showFilters,
        },
      }),
    },
    eBoutiqueServices,
    stores: data.stores,
    locale,
    // Product type context for other components
    productType: config.productType,
    productLabel: config.productLabel,
    // Consumed by the CMS Breadcrumb renderer (BEC-24794).
    breadcrumbs,
  };

  return (
    <div className={styles.page}>
      <AlgoliaDebugEcho entries={data.algoliaDebug ?? []} context={config.routePattern} />
      {/* 1. Breadcrumbs - legacy top-of-page block. Gallet-shaped brands render
          this through the CMS content pipeline instead whenever the content
          has an anchor for it (see placeBreadcrumb above). BEC-24794. */}
      {showBreadcrumbsBlock ? (
        IS_GALLET_SHAPED ? (
          <PdpBreadcrumbsBar items={breadcrumbs} />
        ) : (
          <Breadcrumbs items={breadcrumbs} className={styles.breadcrumbs} />
        )
      ) : null}

      {/* 2. PLPHero - renders between breadcrumbs and filters */}
      {hasHeroMedia ? (
        <PLPHero settings={heroSettings} />
      ) : showSimpleHero && (heroSettings.heading || fallbackTitle) ? (
        <PLPHeroSimple
          title={heroSettings.heading || fallbackTitle}
          subtitle={heroSettings.subheading || undefined}
          textAlignment={heroSettings.textAlignment}
          headingSeoTag={heroSettings.headingSeoTag}
          subheaderIsCapitalized={heroSettings.subheaderIsCapitalized}
          textColor={heroSettings.textColor}
          backgroundColor={heroSettings.backgroundColor}
        />
      ) : null}

      {/* 3. Filter bar - sticky mode (when not inline in ProductGrid) */}
      {showFilters && config.filterBarPosition === 'sticky' && (
        <ListingFilters
          facets={filteredFacets}
          activeFilters={filters}
          totalResults={data.totalHits}
          position="sticky"
          allFacets={filteredAllFacets}
          currentSort={filters.sortBy || 'relevance'}
          productLabel={config.productLabel}
        />
      )}

      {/* 4. CMS Content (services, product grid, breadcrumb, etc.) — falls back
          to just the product grid when there is no CMS content. */}
      <CMSRenderer
        content={contentWithBreadcrumb as unknown as ContentfulFragment[]}
        extraData={extraData}
      />
    </div>
  );
}
